import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MobileBottomNav from "../../components/feature/MobileBottomNav";
import useAuthStatus from "../../hooks/useAuthStatus";
import { getTransactionHistory } from "../../api/apiServices";
import { icons } from "../../utils/icons";
import MarketingSlides from "../../components/feature/MarketingSlides";

const BILL_CATEGORIES = [
  { id: "airtime",     label: "Airtime",     description: "Top up any network",   icon: "ri-phone-line",      tone: "success", path: "/bills/airtime" },
  { id: "data",        label: "Data Bundle", description: "Buy internet data",     icon: "ri-wifi-line",       tone: "info",     path: "/bills/data" },
  { id: "electricity", label: "Electricity", description: "Pay DISCO bills",       icon: "ri-flashlight-line", tone: "warning",  path: "/bills/electricity" },
  { id: "tv",          label: "Cable TV",    description: "DSTV, GOtv, Startimes", icon: "ri-tv-2-line",       tone: "purple",   path: "/bills/tv" },
];
const QUICK_ACTIONS = [
  { label: "MTN Airtime", img: icons.mtn,   path: "/bills/airtime?network=MTN" },
  { label: "EKEDC",       img: icons.ekedc, path: "/bills/electricity?disco=EKEDC" },
  { label: "DSTV",        img: icons.dstv,  path: "/bills/tv?provider=DSTV" },
  { label: "Glo Data",    img: icons.glo,   path: "/bills/data?network=GLO" },
];

function GuestBills({ onSignUp }) {
  const services = [
    { icon: "ri-phone-line",      tone: "success", title: "Airtime Top-Up",    desc: "MTN, Airtel, Glo, 9Mobile — any network, instantly" },
    { icon: "ri-wifi-line",       tone: "info",     title: "Data Bundles",       desc: "Daily, weekly & monthly plans at the best prices" },
    { icon: "ri-flashlight-line", tone: "warning",  title: "Electricity Bills",  desc: "All DISCOs — EKEDC, IKEDC, AEDC, PHED & more" },
    { icon: "ri-tv-2-line",       tone: "purple",   title: "Cable TV",           desc: "DSTV, GOtv & Startimes subscription & renewal" },
  ];
  const perks = [
    { icon: "ri-percent-line",    text: "1% cashback on every bill payment" },
    { icon: "ri-coupon-3-line",   text: "Exclusive coupon discounts available" },
    { icon: "ri-timer-flash-line",text: "Payments delivered in under 10 seconds" },
    { icon: "ri-history-line",    text: "Full transaction history & receipts" },
  ];
  return (
    <div className="px-5 pb-10 pt-5 space-y-6">
      <div className="rounded-2xl overflow-hidden relative" style={{ background: "var(--gradient-hero)" }}>
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-10 pointer-events-none"
          style={{ background: "radial-gradient(circle, #fff, transparent)" }} />
        <div className="relative px-5 py-6 text-center">
          <div className="w-14 h-14 bg-white/15 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <i className="ri-receipt-line text-white text-2xl" />
          </div>
          <h2 className="text-white font-bold text-xl mb-2 font-display">
            Pay All Your Bills in One Place
          </h2>
          <p className="text-sm leading-relaxed mb-5" style={{ color: "var(--gradient-hero-text-muted)" }}>
            Airtime, data, electricity, cable TV — paid instantly with 1% cashback on every transaction.
          </p>
          <button onClick={onSignUp}
            className="w-full py-3.5 bg-white text-emerald-700 font-bold text-sm rounded-2xl active:scale-95 transition-all">
            Sign Up to Pay Bills
          </button>
        </div>
      </div>

      <div>
        <p className="text-muted text-xs font-semibold uppercase tracking-wider mb-3">Available Services</p>
        <div className="grid grid-cols-2 gap-3">
          {services.map(s => (
            <div key={s.title} className="bg-surface rounded-2xl p-4 border border-border">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `var(--color-${s.tone}-light)` }}>
                <i className={`${s.icon} text-lg`} style={{ color: `var(--color-${s.tone})` }} />
              </div>
              <p className="text-primary text-sm font-bold mb-1 font-display">{s.title}</p>
              <p className="text-muted text-xs leading-tight">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-surface rounded-2xl border border-border p-5">
        <p className="text-primary font-bold text-sm mb-4 font-display">Why use Easwallet Bills?</p>
        <div className="space-y-3">
          {perks.map((p, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "var(--color-success-light)" }}>
                <i className={`${p.icon} text-sm`} style={{ color: "var(--color-success)" }} />
              </div>
              <p className="text-secondary text-sm">{p.text}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-muted text-xs font-semibold uppercase tracking-wider mb-3">Quick Access</p>
        <div className="grid grid-cols-4 gap-2">
          {QUICK_ACTIONS.map(a => (
            <button key={a.label} onClick={onSignUp}
              className="flex flex-col items-center gap-2 bg-surface rounded-2xl py-4 px-2 border border-border active:scale-95 transition-all">
              <div className="w-10 h-10 rounded-xl bg-surface-muted flex items-center justify-center overflow-hidden">
                <img src={a.img} alt={a.label} className="w-8 h-8 object-contain" onError={e => { e.target.style.display = "none"; }} />
              </div>
              <span className="text-secondary text-[10px] font-semibold text-center leading-tight">{a.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <button onClick={onSignUp}
          className="w-full py-4 bg-brand hover:bg-brand-hover text-on-brand font-bold text-sm rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2">
          <i className="ri-user-add-line" />Create Free Account
        </button>
        <button onClick={onSignUp}
          className="w-full py-3.5 bg-surface border border-border-strong text-secondary font-semibold text-sm rounded-2xl hover:bg-surface-hover transition-all active:scale-95">
          Sign In to Existing Account
        </button>
      </div>
    </div>
  );
}

function getBillIcon(tx) {
  const name     = (tx.assetName || "").toLowerCase();
  const network  = (tx.metadata?.network  || "").toUpperCase();
  const disco    = (tx.metadata?.disco    || "").toUpperCase();
  const provider = (tx.metadata?.provider || "").toUpperCase();
  if (network === "MTN"     || name.includes("mtn"))        return { img: icons.mtn };
  if (network === "AIRTEL"  || name.includes("airtel"))     return { img: icons.airtel };
  if (network === "GLO"     || name.includes("glo"))        return { img: icons.glo };
  if (network === "9MOBILE" || name.includes("9mobile"))    return { img: null, ri: "ri-phone-line", tone: "success" };
  if (provider === "DSTV"   || name.includes("dstv"))       return { img: icons.dstv };
  if (provider === "GOTV"   || name.includes("gotv"))       return { img: icons.gotv };
  if (provider === "STARTIMES" || name.includes("startimes")) return { img: null, ri: "ri-tv-2-line", tone: "warning" };
  if (disco === "EKEDC" || name.includes("eko"))    return { img: icons.ekedc };
  if (disco === "IKEDC" || name.includes("ikeja"))  return { img: icons.ikedc };
  if (disco === "AEDC"  || name.includes("abuja"))  return { img: icons.aedc };
  if (disco === "IBEDC" || name.includes("ibadan")) return { img: icons.ibedc };
  if (disco === "PHEDC" || name.includes("port"))   return { img: icons.phed };
  if (disco === "KAEDCO"|| name.includes("kaduna")) return { img: icons.kaedco };
  const asset = (tx.asset || "").toUpperCase();
  if (asset === "AIRTIME") return { img: null, ri: "ri-phone-line", tone: "success" };
  if (asset === "DATA")    return { img: null, ri: "ri-wifi-line",  tone: "info" };
  return                          { img: null, ri: "ri-receipt-line", tone: "muted" };
}

const Sk = ({ className = "" }) => <div className={`animate-pulse bg-skeleton rounded-xl ${className}`} />;

function BillIconCell({ tx }) {
  const { img, ri, tone } = getBillIcon(tx);
  const [imgFailed, setImgFailed] = useState(false);
  if (img && !imgFailed) {
    return (
      <div className="w-10 h-10 rounded-xl bg-surface-muted border border-border flex items-center justify-center overflow-hidden flex-shrink-0">
        <img src={img} alt="" className="w-7 h-7 object-contain" onError={() => setImgFailed(true)} />
      </div>
    );
  }
  const t = tone || "muted";
  return (
    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
      style={{ background: t === "muted" ? "var(--color-surface-hover)" : `var(--color-${t}-light)` }}>
      <i className={`${ri || "ri-receipt-line"} text-lg`} style={{ color: t === "muted" ? "var(--color-text-muted)" : `var(--color-${t})` }} />
    </div>
  );
}

export default function BillsPage() {
  const navigate        = useNavigate();
  const isAuthenticated = useAuthStatus();

  const [recentBills,  setRecentBills]  = useState([]);
  const [billsLoading, setBillsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) { setBillsLoading(false); return; }
    setBillsLoading(true);
    getTransactionHistory()
      .then(res => {
        if (res.status === true) {
          setRecentBills(
            (res.data || [])
              .filter(tx => tx.type === "bill_payment")
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 5)
          );
        }
      })
      .catch(console.error)
      .finally(() => setBillsLoading(false));
  }, [isAuthenticated]);

  const formatDate = (iso) => {
    const date = new Date(iso);
    const diff = Date.now() - date;
    const days = Math.floor(diff / 86400000);
    if (days === 0) return `Today, ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    if (days === 1) return "Yesterday";
    return date.toLocaleDateString("en-NG", { day: "numeric", month: "short" });
  };

  return (
    <div className="min-h-screen bg-bg pb-28">
      <div className="bg-surface px-5 pt-12 pb-5 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/home")} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div>
              <h1 className="text-primary font-bold text-lg font-display">Pay Bills</h1>
              <p className="text-muted text-xs">Fast, easy, instant</p>
            </div>
          </div>
          <button onClick={() => navigate("/support")} className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--color-warning-light)" }}>
            <i className="ri-customer-service-2-line text-lg" style={{ color: "var(--color-warning)" }} />
          </button>
        </div>
      </div>

      {!isAuthenticated ? (
        <GuestBills onSignUp={() => navigate("/auth")} />
      ) : (
        <div className="px-5 pt-5 space-y-6">
          <MarketingSlides page="bills" compact />

          <div>
            <p className="text-muted text-xs font-semibold uppercase tracking-wider mb-3">Quick Access</p>
            <div className="grid grid-cols-4 gap-2">
              {QUICK_ACTIONS.map(action => (
                <button key={action.label} onClick={() => navigate(action.path)}
                  className="flex flex-col items-center gap-2 bg-surface rounded-2xl py-4 px-2 border border-border hover:border-brand-border hover:bg-brand-light transition-all active:scale-95">
                  <div className="w-10 h-10 rounded-xl bg-surface-muted flex items-center justify-center overflow-hidden">
                    <img src={action.img} alt={action.label} className="w-8 h-8 object-contain" onError={e => { e.target.style.display = "none"; }} />
                  </div>
                  <span className="text-secondary text-[10px] font-semibold text-center leading-tight">{action.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-muted text-xs font-semibold uppercase tracking-wider mb-3">All Services</p>
            <div className="grid grid-cols-2 gap-3">
              {BILL_CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => navigate(cat.path)}
                  className="bg-surface rounded-2xl p-4 border border-border hover:border-brand-border transition-all active:scale-95 text-left flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `var(--color-${cat.tone}-light)` }}>
                    <i className={`${cat.icon} text-xl`} style={{ color: `var(--color-${cat.tone})` }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-primary font-bold text-sm font-display">{cat.label}</p>
                    <p className="text-muted text-xs mt-0.5 truncate">{cat.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-muted text-xs font-semibold uppercase tracking-wider mb-3">Recent Payments</p>
            {billsLoading ? (
              <div className="bg-surface rounded-2xl border border-border overflow-hidden divide-y divide-border">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3.5">
                    <Sk className="w-10 h-10 rounded-xl flex-shrink-0" />
                    <div className="flex-1 space-y-1.5"><Sk className="h-3 w-28" /><Sk className="h-2.5 w-20" /></div>
                    <div className="space-y-1.5 items-end flex flex-col"><Sk className="h-3 w-14" /><Sk className="h-2.5 w-10" /></div>
                  </div>
                ))}
              </div>
            ) : recentBills.length === 0 ? (
              <div className="bg-surface rounded-2xl border border-border p-8 text-center">
                <div className="w-14 h-14 bg-surface-hover rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <i className="ri-receipt-line text-muted text-2xl" />
                </div>
                <p className="text-secondary font-semibold text-sm mb-1">No bill payments yet</p>
                <p className="text-muted text-xs">Your recent bill payments will appear here</p>
              </div>
            ) : (
              <div className="bg-surface rounded-2xl border border-border overflow-hidden divide-y divide-border">
                {recentBills.map(tx => (
                  <div key={tx._id} onClick={() => navigate(`/transaction/${tx._id}`)}
                    className="flex items-center gap-3 px-4 py-3.5 cursor-pointer hover:bg-surface-hover transition-colors">
                    <BillIconCell tx={tx} />
                    <div className="flex-1 min-w-0">
                      <p className="text-primary text-sm font-semibold truncate">{tx.assetName || tx.asset}</p>
                      <p className="text-muted text-xs">
                        {formatDate(tx.createdAt)}
                        {tx.metadata?.phone && <span className="font-mono"> · {tx.metadata.phone}</span>}
                        {tx.metadata?.discount > 0 && <span className="ml-1 font-semibold" style={{ color: "var(--color-success)" }}>· ₦{tx.metadata.discount} off</span>}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-primary text-sm font-bold">₦{tx.nairaAmount.toLocaleString()}</p>
                      <span className="text-xs font-semibold" style={{
                        color: tx.status === "completed" ? "var(--color-success)" : tx.status === "pending" ? "var(--color-warning)" : "var(--color-danger)"
                      }}>
                        {tx.status === "completed" ? "Paid" : tx.status === "pending" ? "Pending" : tx.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <MobileBottomNav />
    </div>
  );
}
